import { ToSmartSendItem } from './ToSmartSendItem';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { SearchObject } from '~/components/search/algolia/Models';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { useCallback } from 'react';
import { SmartSendItemsCache } from '../../lib/cache/SmartSendItemsCache';

export interface SearchObjectResponse {
  items: SmartSendItemApiModel[];
  totalItems: number;
  totalPages: number;
}

const useSearchObjectsToSmartSendItems = () => {
  const { getItemsByIds } = useSmartSendApi();

  const searchObjectsToSmartSendItems = useCallback(
    async (hits: SearchObject[], totalHits: number, totalPages: number): Promise<SearchObjectResponse> => {
      // Stores the index of the item in the output array
      const itemOutputIndexes: { [key: string]: number } = {};
      const output: SmartSendItemApiModel[] = [];
      const ids = [];
      hits.forEach((h, index) => {
        output.push(ToSmartSendItem(h));

        // If this is a SmartSend item, store the id and get the full object later
        if (h.smartSend?.count > 0) {
          const importedItemId =
            h.smartSend.items.find((x) => x.source?.toLowerCase() == 'grouped')?.id || h.smartSend.items[0].id;
          itemOutputIndexes[importedItemId] = index;
          ids.push(importedItemId);
        }
      });

      if (ids.length > 0) {
        const getItemsResponse = await getItemsByIds(ids);
        if (getItemsResponse?.success) {
          getItemsResponse.result.Items.forEach((ssi) => {
            const mapped: SmartSendItemApiModel = { ...output[itemOutputIndexes[ssi.Id]] };

            // Copy the P2G Ref from the mapped object if not present on the ssi, as this is not returned from the API but is needed for the UI
            const bookingDetails = { ...ssi.BookingDetails };
            if (!bookingDetails?.Reference && mapped.BookingDetails?.Reference) {
              bookingDetails.Reference = mapped.BookingDetails.Reference;
            }

            output[itemOutputIndexes[ssi.Id]] = {
              ...ssi,
              BookingDetails: bookingDetails,
              // The ssi wont have the sender. This is usally set client side when creating the item, but not on the list get.
              // In this case use the one from the search hit.
              Sender: {
                ...ssi.Sender,
                ...mapped.Sender
              },
              Recipient: {
                ...ssi.Recipient,
                ...mapped.Recipient
              }
            };

            // Refresh Cache if we have updated BookingDetails
            if (ssi?.BookingDetails?.OrderLineId) {
              SmartSendItemsCache.forEach((item) => {
                if (item.Id === ssi.Id && !item.BookingDetails?.OrderLineId) {
                  item.BookingDetails = { ...ssi.BookingDetails };
                }
              });
            }
          });
        }
      }

      return { items: output.filter((x) => !!x), totalItems: totalHits, totalPages };
    },
    [getItemsByIds]
  );

  return { searchObjectsToSmartSendItems } as const;
};

export default useSearchObjectsToSmartSendItems;
